/**
 * 이상한 방 - 꾸미기 띠 (전원이 함께 방을 채우는 1분의 하단 도구 띠, 순수 DOM)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(꾸미기 1분) · 4.1절(인당 배치 수)
 *            `gameHud.css` 머리말(화면 중앙은 비운다 · 누르는 것은 44px · 색은 `--gh-*`)
 *            로드맵 M10
 *
 * ## 이 띠는 계산하지 않는다
 *
 * 받은 뷰(`DecorateBarView`)를 그린다. 남은 개수도 한도도 고른 소품도 전부 도구(`input/decorateTool.ts`)가 내린 값이고
 * 그 값의 원본은 호스트다(`self.placementsLeft` · `self.placementsLimit`). 여기서 `limit - used`를 다시 세면
 * 규칙이 화면에 두 벌째로 생긴다(`changerBar.ts`와 같은 규약).
 *
 * ## 바꾸기 띠의 축소판이다 - 동사도 색도 없다
 *
 * 꾸미기의 동작은 둘뿐이다(놓기 · 걷기). 그래서 동사 팔레트도 색 팔레트도 되돌리기도 없다 - 걷기는 놓은 것을 다시
 * 누르는 것이고 그것이 곧 되돌리기다. 남는 것은 **개수 점 · 소품 스트립 · [다 놓았다]** 셋이고, 스트립은 바꾸기 띠와
 * 같은 조각을 같은 손으로 그린다(`ChangerCatalogView`).
 *
 ## [다 놓았다]는 이 띠에 없다 - HUD가 이미 그것이다

 * 바꾸기 띠의 [다 했다]는 커밋이지만 꾸미기의 "다 놓았다"는 **투표**다(`phase.decorate.skip` → `onSkipVote`).
 * 그 투표 버튼은 HUD가 이미 그리고 있고 몇 표인지도 거기서 센다(`gh-skip`). 여기에 하나 더 두면 같은 투표를 부르는
 * 버튼이 화면에 둘이 되고, 그중 하나만 표 수를 말하는 순간 어느 쪽이 참인지가 화면에서 갈린다.
 *
 * ## 자리: 하단 띠. 바꾸기 띠와 같은 자리를 쓴다
 *
 * 두 띠는 같은 화면에 설 수 없다(페이즈가 다르다). 그래서 같은 좌표·같은 높이 변수(`--cb-height`)를 쓴다 -
 * 카메라 묶음이 어느 띠 위에 서는지를 몰라도 되게 하기 위해서다.
 */

import type { CatalogId } from '../core/types'
import { t } from '../i18n'
import { itemName } from '../i18n/itemName'
import type { DecorateBarView } from '../input/decorateTool'
import './decorateBar.css'
import { createElement, setAttr, setFlag, setText } from './dom'
import { publishSize } from './overlayLayout'

/* --- 타입 ------------------------------------------------------------------------ */

export type DecorateBarHandlers = Readonly<{
  onCatalog: (id: CatalogId) => void
}>

export type DecorateBarOptions = Readonly<{
  mount: HTMLElement
  handlers: DecorateBarHandlers
}>

export type DecorateBarHandle = Readonly<{
  root: HTMLElement
  render: (view: DecorateBarView) => void
  dispose: () => void
}>

/** 색 정수 → CSS. `changerBar`와 같은 한 줄이다(둘 다 팔레트 값을 인라인으로 칠한다). */
function toCssColor(hex: number): string {
  return `#${hex.toString(16).padStart(6, '0')}`
}

/* --- 본체 ------------------------------------------------------------------------ */

export function createDecorateBar(options: DecorateBarOptions): DecorateBarHandle {
  const { handlers } = options
  const root = createElement('section', 'db-root')
  root.setAttribute('role', 'toolbar')

  /* 위 줄: 남은 개수 점 · 안내 한 줄 */
  const top = createElement('div', 'db-top')
  const dots = createElement('div', 'db-dots')
  dots.setAttribute('role', 'status')
  const dotNodes: HTMLElement[] = []
  const hint = createElement('p', 'db-hint', '')
  top.append(dots, hint)

  /* 아래 줄: 소품 스트립. 여덟 칩이 가로로 흐른다. */
  const strip = createElement('div', 'db-strip')
  strip.setAttribute('role', 'listbox')
  const chips = new Map<CatalogId, HTMLButtonElement>()

  root.append(top, strip)
  options.mount.append(root)

  // 바꾸기 띠와 **같은 변수**를 내보낸다 - 두 띠는 같은 자리에 서고 위의 것들은 어느 쪽인지 몰라도 된다.
  const size = publishSize(root, { height: '--cb-height' })

  /** 점 하나 = 아직 놓을 수 있는 한 개. 한도가 바뀌면(다른 판) 개수를 맞춘다. */
  function paintDots(view: DecorateBarView): void {
    while (dotNodes.length < view.limit) {
      const dot = createElement('i', 'db-dot')
      dot.setAttribute('aria-hidden', 'true')
      dots.append(dot)
      dotNodes.push(dot)
    }
    while (dotNodes.length > view.limit) dotNodes.pop()?.remove()
    dotNodes.forEach((dot, index) => setFlag(dot, 'is-on', index < view.left))
    // 점은 보조기술에 세지 않는다 - 대신 이 한 줄이 같은 말을 한다.
    setAttr(dots, 'aria-label', t('decorate.left', { count: view.left }))
  }

  function paintStrip(view: DecorateBarView): void {
    for (const item of view.strip) {
      let button = chips.get(item.id)
      if (button === undefined) {
        const created = createElement('button', 'db-btn db-chip')
        created.type = 'button'
        created.setAttribute('role', 'option')
        created.addEventListener('click', () => handlers.onCatalog(item.id))
        const swatch = createElement('i', 'db-chip__swatch')
        swatch.setAttribute('aria-hidden', 'true')
        const label = createElement('span', 'db-chip__name')
        created.append(swatch, label)
        chips.set(item.id, created)
        strip.append(created)
        button = created
      }
      const swatch = button.firstElementChild
      if (swatch instanceof HTMLElement) swatch.style.background = toCssColor(item.colorHex)
      // 이름은 사전이 낸다 - 뷰가 실어 오는 것은 id뿐이다(`changerBar`와 같은 규칙).
      const name = itemName(item.id) ?? ''
      const label = button.lastElementChild
      if (label instanceof HTMLElement) setText(label, name)
      setAttr(button, 'aria-label', name)
      setAttr(button, 'aria-selected', item.selected ? 'true' : 'false')
      setFlag(button, 'is-selected', item.selected)
    }
  }

  return {
    root,

    render(view: DecorateBarView): void {
      paintDots(view)
      paintStrip(view)
      /*
       * 안내는 손이 지금 무엇인지를 말한다. 소품을 들었으면 "놓아라", 손이 비었으면 "골라라",
       * 다 놓았으면 "누르면 걷는다" - 다 쓴 사람에게 계속 "놓아라"라고 하면 그것이 곧 막다른 화면이다.
       */
      const hintKey = view.left <= 0 ? 'decorate.hint.spent' : view.picked === null ? 'decorate.hint.pick' : 'decorate.hint.place'
      setText(hint, t(hintKey))

      size.refresh()
    },

    dispose(): void {
      size.dispose()
      root.remove()
    },
  }
}
